<template>
  <main class="trukkin-one-page">
    <div class="trukkin-one-background" aria-hidden="true">
      <div class="trukkin-one-grid"></div>
    </div>

    <header class="trukkin-one-topbar">
      <a class="trukkin-one-brand" :href="homeUrl" aria-label="Trukkin One home">
        <img :src="logoSrc" alt="Trukkin" />
      </a>
    </header>

    <section class="trukkin-one-main">
      <div class="trukkin-one-intro">
        <img class="trukkin-one-logo" :src="logoSrc" alt="Trukkin" />
        <div class="trukkin-one-eyebrow">One &middot; Platform</div>
        <h1>Choose a module to enter</h1>
        <p>Select a product to open its workspace.</p>
      </div>

      <div class="trukkin-one-tiles" aria-label="Product modules">
        <a
          v-for="product in products"
          :key="product.key"
          class="trukkin-one-tile"
          :class="[product.className, { 'trukkin-one-tile--unavailable': product.unavailable }]"
          :href="product.href || undefined"
          :target="product.href ? '_blank' : undefined"
          :rel="product.href ? 'noopener noreferrer' : undefined"
          :aria-label="product.unavailable ? `${product.name} unavailable` : `Open ${product.name}`"
          :aria-disabled="product.unavailable ? 'true' : undefined"
          @click="handleCardClick(product, $event)"
        >
          <div class="trukkin-one-tile__top">
            <div class="trukkin-one-tile__icon">
              <i class="material-icons">{{ product.icon }}</i>
            </div>
            <span class="trukkin-one-tile__badge">
              <i class="material-icons">{{ product.unavailable ? "block" : "check" }}</i>
              {{ product.unavailable ? "Unavailable" : "Available" }}
            </span>
          </div>

          <div class="trukkin-one-tile__body">
            <div class="trukkin-one-tile__title">{{ product.name }}</div>
            <div class="trukkin-one-tile__meta">
              <span class="trukkin-one-tile__tag">{{ product.tag }}</span>
              <span class="trukkin-one-tile__cta">
                {{ product.unavailable ? "Coming soon" : "Enter" }}
                <i class="material-icons">{{ product.unavailable ? "schedule" : "arrow_forward" }}</i>
              </span>
            </div>
          </div>
        </a>
      </div>
    </section>

    <footer class="trukkin-one-footer">
      <span>TRUKKIN.ONE</span>
      <span><i class="material-icons">dashboard</i>Unified product hub</span>
    </footer>
  </main>
</template>

<script>
import logoSrc from "@/assets/Trukkin_logo.png";
import { StorageKeys } from "@/constants/constants";

export default {
  name: "TrukkinOneFrame",
  data() {
    return {
      logoSrc,
      homeUrl: "https://trukkin.one",
    };
  },
  computed: {
    products() {
      return [
        {
          key: "radar",
          name: "Radar",
          tag: "Live tracking",
          icon: "radar",
          className: "trukkin-one-tile--radar",
          href: null,
          unavailable: true,
        },
        {
          key: "missionControl",
          name: "Mission Control",
          tag: "Operations",
          icon: "business_center",
          className: "trukkin-one-tile--mission",
          href: this.missionControlUrl,
        },
        {
          key: "accountsFinance",
          name: "Accounts & Finance",
          tag: "Billing & settlements",
          icon: "account_balance_wallet",
          className: "trukkin-one-tile--accounts",
          href: "https://login.realbooks.in/AMS-war/",
        },
        {
          key: "demandDashboard",
          name: "Demand Dashboard",
          tag: "Inquiry intelligence",
          icon: "psychology",
          className: "trukkin-one-tile--demand",
          href: "https://inquiry.trukkin.ai/login",
        },
        {
          key: "analytics",
          name: "Analytics",
          tag: "Performance insights",
          icon: "show_chart",
          className: "trukkin-one-tile--analytics",
          href: "https://login.qlik.com",
        },
      ];
    },
    missionControlUrl() {
      const missionRoute = localStorage.getItem(StorageKeys.SessionKey)
        ? "jobs"
        : "auth";

      if (window.location.protocol === "file:") {
        return `http://localhost:8080/#/${missionRoute}`;
      }

      const origin =
        window.location.origin && window.location.origin !== "null"
          ? window.location.origin
          : "http://localhost:8080";
      return `${origin}/#/${missionRoute}`;
    },
  },
  methods: {
    handleCardClick(product, event) {
      if (product.unavailable) event.preventDefault();
    },
  },
};
</script>

<style scoped>
.trukkin-one-page {
  --page-bg: #05070e;
  --panel: rgba(20, 27, 46, 0.72);
  --panel-deep: rgba(11, 16, 30, 0.82);
  --text: #e7ecf7;
  --text-strong: #f4f7ff;
  --text-muted: #8b98b8;
  --line: rgba(255, 255, 255, 0.11);
  position: relative;
  display: flex;
  min-height: 100vh;
  min-height: 100svh;
  width: 100%;
  overflow: hidden;
  flex-direction: column;
  isolation: isolate;
  background:
    radial-gradient(1100px 720px at 12% 2%, rgba(31, 69, 199, 0.3), transparent 60%),
    radial-gradient(900px 640px at 92% 100%, rgba(234, 122, 28, 0.15), transparent 60%),
    #05070e;
  color: var(--text);
  font-family: Inter, "Open Sans", "Segoe UI", Arial, sans-serif;
}

.trukkin-one-page::after {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: radial-gradient(130% 120% at 50% 40%, transparent 58%, rgba(3, 5, 12, 0.82));
  content: "";
  pointer-events: none;
}

.trukkin-one-background {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.trukkin-one-grid {
  --grid-cell-size: clamp(56px, 4vw, 64px);
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(120, 150, 230, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(120, 150, 230, 0.07) 1px, transparent 1px);
  background-position: 0 0, 0 0;
  background-size: var(--grid-cell-size) var(--grid-cell-size);
  mask-image: radial-gradient(92% 78% at 50% 30%, #000 42%, transparent 92%);
  -webkit-mask-image: radial-gradient(92% 78% at 50% 30%, #000 42%, transparent 92%);
  animation: trukkin-one-grid-drift 7s linear infinite;
  will-change: background-position;
}

@keyframes trukkin-one-grid-drift {
  to {
    background-position: 0 var(--grid-cell-size), var(--grid-cell-size) 0;
  }
}

.trukkin-one-topbar {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  padding: 22px 40px;
}

.trukkin-one-brand {
  display: flex;
  align-items: center;
}

.trukkin-one-brand img {
  width: auto;
  height: 22px;
  opacity: 0.92;
}

.trukkin-one-main {
  position: relative;
  z-index: 2;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 40px 42px;
}

.trukkin-one-intro {
  max-width: 780px;
  margin-bottom: 40px;
  text-align: center;
}

.trukkin-one-logo {
  display: block;
  width: min(360px, 74vw);
  height: auto;
  margin: 0 auto 16px;
  filter: drop-shadow(0 10px 30px rgba(234, 122, 28, 0.42));
}

.trukkin-one-eyebrow,
.trukkin-one-tile__tag,
.trukkin-one-tile__badge,
.trukkin-one-footer {
  font-family: "SF Mono", "Roboto Mono", ui-monospace, Consolas, monospace;
  text-transform: uppercase;
}

.trukkin-one-eyebrow {
  margin-bottom: 16px;
  color: #8fa0c8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.28em;
}

.trukkin-one-intro h1 {
  margin: 0;
  color: var(--text-strong);
  font-size: clamp(28px, 4vw, 34px);
  font-weight: 800;
  line-height: 1.1;
}

.trukkin-one-intro p {
  max-width: 650px;
  margin: 12px auto 0;
  color: var(--text-muted);
  font-size: 14.5px;
  line-height: 1.55;
}

.trukkin-one-tiles {
  display: grid;
  width: min(1380px, 100%);
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
}

.trukkin-one-tile {
  --accent: #ea7a1c;
  --glow: rgba(234, 122, 28, 0.35);
  --icon-bg: rgba(234, 122, 28, 0.12);
  position: relative;
  display: flex;
  min-height: 190px;
  flex-direction: column;
  overflow: hidden;
  padding: 24px;
  border: 1px solid var(--line);
  border-radius: 20px;
  background: linear-gradient(180deg, var(--panel), var(--panel-deep));
  box-shadow: 0 24px 60px -34px rgba(0, 0, 0, 0.8);
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.trukkin-one-tile::before {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: var(--glow);
  content: "";
  filter: blur(50px);
  opacity: 0.5;
  pointer-events: none;
}

.trukkin-one-tile:hover,
.trukkin-one-tile:focus-visible {
  border-color: var(--accent);
  box-shadow: 0 30px 70px -30px var(--glow);
  transform: translateY(-6px);
}

.trukkin-one-tile--unavailable {
  --accent: #5e6c90;
  --glow: rgba(120, 140, 180, 0.16);
  --icon-bg: rgba(255, 255, 255, 0.04);
  cursor: not-allowed;
  opacity: 0.72;
}

.trukkin-one-tile--unavailable:hover,
.trukkin-one-tile--unavailable:focus-visible {
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 60px -34px rgba(0, 0, 0, 0.8);
  transform: none;
}

.trukkin-one-tile__top {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.trukkin-one-tile__icon {
  display: grid;
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border: 1px solid var(--line);
  border-radius: 15px;
  background: var(--icon-bg);
  box-shadow: 0 6px 18px -8px var(--glow);
  color: var(--accent);
  place-items: center;
}

.trukkin-one-tile__icon .material-icons {
  font-size: 28px;
}

.trukkin-one-tile__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 23px;
  padding: 4px 9px;
  border: 1px solid rgba(52, 211, 153, 0.24);
  border-radius: 999px;
  background: rgba(52, 211, 153, 0.1);
  color: #8fd9be;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  white-space: nowrap;
}

.trukkin-one-tile__badge .material-icons,
.trukkin-one-tile__cta .material-icons,
.trukkin-one-footer .material-icons {
  font-size: 14px;
}

.trukkin-one-tile--unavailable .trukkin-one-tile__badge {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #7c8bb0;
}

.trukkin-one-tile__body {
  position: relative;
  margin-top: auto;
  padding-top: 22px;
}

.trukkin-one-tile__title {
  color: var(--text-strong);
  font-size: 21px;
  font-weight: 800;
  line-height: 1.2;
}

.trukkin-one-tile--unavailable .trukkin-one-tile__title {
  color: #8b98b8;
}

.trukkin-one-tile__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-top: 10px;
}

.trukkin-one-tile__tag {
  color: #7c8bb0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.trukkin-one-tile__cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--accent);
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.trukkin-one-tile--radar {
  --accent: #38bdf8;
  --glow: rgba(56, 189, 248, 0.35);
  --icon-bg: rgba(56, 189, 248, 0.12);
}

.trukkin-one-tile--mission {
  --accent: #ea7a1c;
  --glow: rgba(234, 122, 28, 0.35);
  --icon-bg: rgba(234, 122, 28, 0.12);
}

.trukkin-one-tile--accounts {
  --accent: #34d399;
  --glow: rgba(52, 211, 153, 0.3);
  --icon-bg: rgba(52, 211, 153, 0.12);
}

.trukkin-one-tile--demand {
  --accent: #a78bfa;
  --glow: rgba(167, 139, 250, 0.28);
  --icon-bg: rgba(167, 139, 250, 0.12);
}

.trukkin-one-tile--analytics {
  --accent: #f472b6;
  --glow: rgba(244, 114, 182, 0.3);
  --icon-bg: rgba(244, 114, 182, 0.12);
}

.trukkin-one-footer {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 40px 24px;
  color: #5e6c90;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.trukkin-one-footer span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

@media (max-width: 1280px) {
  .trukkin-one-tiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 880px) {
  .trukkin-one-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .trukkin-one-topbar,
  .trukkin-one-footer {
    padding-right: 20px;
    padding-left: 20px;
  }

  .trukkin-one-main {
    justify-content: flex-start;
    padding: 42px 20px 36px;
  }

  .trukkin-one-intro {
    margin-bottom: 28px;
  }

  .trukkin-one-tiles {
    grid-template-columns: 1fr;
  }

  .trukkin-one-tile {
    min-height: 172px;
    border-radius: 16px;
  }

  .trukkin-one-footer {
    align-items: flex-start;
    flex-direction: column;
    line-height: 1.6;
  }
}

@media (prefers-reduced-motion: reduce) {
  .trukkin-one-grid {
    animation: none;
  }
}
</style>
